import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import type { GroupBase, Props } from "react-select";
import { useEffect, useState } from "react";

export interface SelectCustomProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends Props<Option, IsMulti, Group> {
  creatable?: boolean;
}

function SelectCustom<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(allProps: SelectCustomProps<Option, IsMulti, Group>) {
  const { creatable, ...props } = allProps;
  const Component = creatable ? CreatableSelect : Select;
  const [isMounted, setIsMounted] = useState(false);

  // Must be deleted once
  // https://github.com/JedWatson/react-select/issues/5459 is fixed.
  useEffect(() => setIsMounted(true), []);

  if (!isMounted) {
    return null;
  }

  return (
    <Component
      // These two options seems magical, they make the dropdown appear on top of everything
      // https://stackoverflow.com/a/64973481/978690
      // menuPosition="fixed"
      // menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      styles={filterStyles}
      placeholder="Choisir..."
      noOptionsMessage={() => "Aucun résultat"}
      formatCreateLabel={(inputValue) => `Ajouter "${inputValue}"`}
      theme={(defaultTheme) => ({
        ...defaultTheme,
        colors: {
          ...defaultTheme.colors,
          primary: "#1752B7", // primary blue
          primary25: "hsl(217 91% 95%)", // very light blue
          primary50: "hsl(217 91% 90%)", // light blue
          primary75: "hsl(217 91% 80%)", // medium light blue
        },
      })}
      instanceId={props.name}
      inputId={props.inputId}
      classNamePrefix={props.classNamePrefix}
      {...props}
    />
  );
}

const filterStyles = {
  // control: (styles) => ({ ...styles, borderWidth: 0 }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: (styles: any, state: any) => ({
    ...styles,
    color: "#475569", // slate-600
    "&:hover": {
      borderColor: state.isFocused ? "#1752B7" : styles.borderColor, // primary blue
    },
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  placeholder: (styles: any) => ({
    ...styles,
    color: "#64748b", // slate-500
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  singleValue: (styles: any) => ({
    ...styles,
    color: "#475569", // slate-600
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  multiValueLabel: (styles: any) => ({
    ...styles,
    color: "#475569", // slate-600
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  option: (styles: any, state: any) => ({
    ...styles,
    color:
      state.isSelected || state.isFocused
        ? "#1752B7" // primary blue when selected/focused
        : "#475569", // slate-600 for normal state
    backgroundColor: state.isSelected
      ? "hsl(217 91% 95%)" // very light blue when selected
      : state.isFocused
        ? "hsl(217 91% 98%)" // even lighter blue when focused
        : "transparent",
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  indicatorSeparator: (styles: any) => ({
    ...styles,
    borderWidth: 0,
    backgroundColor: "transparent",
  }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  menuPortal: (provided: any) => ({ ...provided, zIndex: 10000 }),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  menu: (provided: any) => ({ ...provided, zIndex: 10000 }),
};

export default SelectCustom;
